import type { Dispatch, SetStateAction } from 'react'
import {
  simulateDeadline as simulateDeadlineAll,
  revealResults as revealResultsAll,
  finalizeGameweek as finalizeGameweekAll,
  simulateSeason as simulateSeasonAll,
  simulateGameweekLive,
  resetAllData,
} from '../api/dev'
import { loadLeagues } from '../api/leagues'
import {
  loadPredictions,
  savePredictions,
  loadBankerGameId,
  loadSeasonHistory,
  loadSeasonDetails,
  loadCurrentGameweek,
  clearRevealSeen,
} from '../storage'
import { getActualResultsByGameweek } from '../seasonData'
import { applyAutoPredict } from '../autoPredict'
import { isDevToolAvailable, type DevTool } from '../devToolCapabilities'
import type { DataMode } from '../dataMode'
import { useReplicaGameweekRun } from './useReplicaGameweekRun'
import type { GameweekSimulationController } from './useGameweekSimulation'
import type { Game, GameweekDetail, GameweekResult, League, Prediction } from '../types'

export interface UseDevToolsOptions {
  /** Every handler below is a no-op for anyone but the admin. Means exactly that
   *  now — the mode half of the old "admin AND not live" is `mode` below. */
  isAdmin: boolean
  /** Which world the server is running in; each handler is gated on its own tool's capability. */
  mode: DataMode | null
  currentGameweek: number
  games: Game[]
  predictions: Record<string, Prediction>
  bankerGameId: string | null
  resultsRevealed: boolean
  simulation: GameweekSimulationController
  setCurrentGameweek: Dispatch<SetStateAction<number>>
  setPredictions: Dispatch<SetStateAction<Record<string, Prediction>>>
  setBankerGameId: Dispatch<SetStateAction<string | null>>
  setResultsRevealed: Dispatch<SetStateAction<boolean>>
  markDeadlinePassed: (gameweek: number) => void
  setSeasonHistory: Dispatch<SetStateAction<GameweekResult[]>>
  setSeasonDetails: Dispatch<SetStateAction<Record<number, GameweekDetail>>>
  setLeagues: Dispatch<SetStateAction<League[]>>
  onToast: (message: string) => void
}

export interface DevToolsController {
  simulateDeadline: () => void
  toggleResults: () => void
  finalizeGameweek: () => void
  simulateSeason: () => void
  simulateGameweekRealtime: () => void
  /** A replica run is in flight — pressing the control again freezes the clock. */
  isRunningGameweek: boolean
  changeGameweek: (delta: number) => void
  /** The typed token live mode requires; the rail collects it. */
  resetAll: (confirm?: string) => void
  clearRevealSeenForGameweek: () => void
}

/**
 * The admin dev tools, which are the app's internal season simulation for
 * EVERY user — real accounts and the 10 bots — driven server-side. Each handler is
 * thin: trigger the server tick, then re-fetch the admin's own view so they see the shared world
 * after it.
 *
 * Every handler used to open with its own `if (!isAdmin) return`. That guard now lives in one
 * wrapper, so a handler added later cannot forget it — and the same wrapper also checks the tool
 * against the running mode, because "may the admin press this"
 * has two halves and only one of them is about who they are.
 */
export function useDevTools({
  isAdmin,
  mode,
  currentGameweek,
  games,
  predictions,
  bankerGameId,
  resultsRevealed,
  simulation,
  setCurrentGameweek,
  setPredictions,
  setBankerGameId,
  setResultsRevealed,
  markDeadlinePassed,
  setSeasonHistory,
  setSeasonDetails,
  setLeagues,
  onToast,
}: UseDevToolsOptions): DevToolsController {
  /**
   * Wraps a handler so it is inert unless the caller is the admin **and** the tool is available in
   * the running mode — the single dev-tools gate, so a handler added later
   * cannot forget either half. The server refuses the same request independently; this is what
   * stops the client firing one it knows will be refused.
   */
  // Always called, gated at use: `Play live` is one button with two implementations.
  const replicaRun = useReplicaGameweekRun({ onToast })

  const gated =
    <A extends unknown[]>(tool: DevTool, run: (...args: A) => void | Promise<void>) =>
    (...args: A): void => {
      if (!isAdmin || !isDevToolAvailable(tool, mode)) return
      void run(...args)
    }

  return {
    isRunningGameweek: replicaRun.isRunning,

    simulateDeadline: gated('simulateDeadline', async () => {
      markDeadlinePassed(currentGameweek)
      try {
        await simulateDeadlineAll(currentGameweek)
        const [refreshedPredictions, refreshedBanker, leaguesData] = await Promise.all([
          loadPredictions(currentGameweek),
          loadBankerGameId(currentGameweek),
          loadLeagues(),
        ])
        setPredictions(refreshedPredictions)
        setBankerGameId(refreshedBanker)
        setLeagues(leaguesData)
      } catch (error) {
        console.error('Failed to simulate the deadline', error)
      }
    }),

    // "Reveal results" flips the shared "results are in" flag for EVERY user —
    // distinct from finalize, which also scores + advances. Persisting it server-side is what lets
    // other users' clients (via the poll) surface the revealed results and their live points.
    toggleResults: gated('revealResults', async () => {
      const next = !resultsRevealed
      setResultsRevealed(next)
      try {
        await revealResultsAll(currentGameweek, next)
      } catch {
        setResultsRevealed(!next) // revert on failure
        onToast('Failed to reveal results.')
      }
    }),

    finalizeGameweek: gated('finalizeGameweek', async () => {
      if (!resultsRevealed) return
      const nextGameweek = currentGameweek + 1
      try {
        // Server scores this gameweek for every user + bot and advances the shared gameweek.
        await finalizeGameweekAll(currentGameweek)
        const [history, details, nextPredictions, nextBanker, leaguesData] = await Promise.all([
          loadSeasonHistory(),
          loadSeasonDetails(),
          loadPredictions(nextGameweek),
          loadBankerGameId(nextGameweek),
          loadLeagues(),
        ])
        setSeasonHistory(history)
        setSeasonDetails(details)
        setCurrentGameweek(nextGameweek)
        setPredictions(nextPredictions)
        setBankerGameId(nextBanker)
        setLeagues(leaguesData)
        setResultsRevealed(false)
      } catch (error) {
        console.error('Failed to finalize Gameweek ' + currentGameweek, error)
      }
    }),

    simulateSeason: gated('simulateSeason', async () => {
      try {
        // Server loops deadline + finalize to the final gameweek for every user + bot, then lands
        // the shared current gameweek. Re-fetch the admin's own view to reflect the built-up
        // season.
        await simulateSeasonAll()
        const [history, details, gw, leaguesData] = await Promise.all([
          loadSeasonHistory(),
          loadSeasonDetails(),
          loadCurrentGameweek(currentGameweek),
          loadLeagues(),
        ])
        const [nextPredictions, nextBanker] = await Promise.all([
          loadPredictions(gw.gameweek),
          loadBankerGameId(gw.gameweek),
        ])
        setSeasonHistory(history)
        setSeasonDetails(details)
        setCurrentGameweek(gw.gameweek)
        setPredictions(nextPredictions)
        setBankerGameId(nextBanker)
        setLeagues(leaguesData)
        setResultsRevealed(false)
      } catch (error) {
        console.error('Failed to simulate the season', error)
      }
    }),

    simulateGameweekRealtime: gated('playLive', () => {
      if (simulation.isSimulating || games.length === 0) return

      /**
       * In `replica` nothing is simulated: a real pipeline runs against a controlled clock.
       * Named `runGameweek` there so the two are not confused in a stack
       * trace — `simulateGameweek*` describes the mock behaviour and only the mock behaviour.
       *
       * The admin's own picks are saved as they stand, with no auto-fill and no auto-Banker: the
       * deadline job fills everyone once the derived deadline passes, and a second writer here is
       * exactly what this epic exists to remove.
       */
      if (mode === 'replica') {
        const gameweek = currentGameweek
        void (async () => {
          try {
            await savePredictions(gameweek, predictions)
          } catch (error) {
            console.error('Failed to save predictions before running the gameweek', error)
          }
          await replicaRun.run(gameweek)
        })()
        return
      }

      const { updatedPredictions, autoPredictedGameIds } = applyAutoPredict(games, predictions)

      markDeadlinePassed(currentGameweek)
      setPredictions(updatedPredictions)

      // A Banker that landed on an auto-filled fixture is dropped, and nothing replaces it: no
      // Banker is ever picked for a player (reverses the safety net). Reaching
      // the deadline without one is a real decision with a real cost, and the post-deadline
      // "Banker was not selected" row is what that player sees. The server does the same for
      // everyone else in the tick below, so both sides agree on "none".
      const bankerSurvives =
        bankerGameId !== null && !autoPredictedGameIds.includes(bankerGameId)
      if (!bankerSurvives) {
        setBankerGameId(null)
      }

      const gameweek = currentGameweek

      // Persist the admin's own now-locked picks, then run the whole-app deadline + finalize tick
      // server-side. Saving first means the server scores the admin against exactly what they see
      // animate here, and only fills OTHER users' gaps. The tick runs in the
      // background while the local goal-by-goal playback plays out.
      const serverTick = (async () => {
        await savePredictions(gameweek, updatedPredictions)
        await simulateGameweekLive(gameweek)
      })()

      simulation.start({
        games,
        results: getActualResultsByGameweek()[gameweek] ?? {},
        onFinish: async () => {
          setResultsRevealed(true)
          try {
            // Await the tick before re-fetching, so the admin's season stats and leagues reflect
            // the finalized shared world rather than the state just before it.
            await serverTick
            const [history, details, leaguesData] = await Promise.all([
              loadSeasonHistory(),
              loadSeasonDetails(),
              loadLeagues(),
            ])
            setSeasonHistory(history)
            setSeasonDetails(details)
            setLeagues(leaguesData)
          } catch (error) {
            console.error('Failed to sync the live gameweek simulation', error)
          }
        },
      })
    }),

    changeGameweek: gated('changeGameweek', async (delta: number) => {
      // Stepping gameweeks by hand abandons any playback rather than letting it finish against
      // fixtures that are no longer on screen.
      simulation.stop()
      const newGameweek = Math.max(1, currentGameweek + delta)
      try {
        const [preds, banker] = await Promise.all([
          loadPredictions(newGameweek),
          loadBankerGameId(newGameweek),
        ])
        setCurrentGameweek(newGameweek)
        setPredictions(preds)
        setBankerGameId(banker)
        setResultsRevealed(false)
      } catch {
        onToast('Failed to switch gameweek.')
      }
    }),

    // The token is collected by the rail's confirmation and forwarded unchanged.
    resetAll: gated('resetAll', async (confirm?: string) => {
      try {
        await resetAllData(confirm)
      } finally {
        window.location.reload() // dev
      }
    }),

    clearRevealSeenForGameweek: gated('clearRevealSeen', async () => {
      try {
        await clearRevealSeen(currentGameweek)
      } finally {
        window.location.reload()
      }
    }),
  }
}
